import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode, AnchorHTMLAttributes } from 'react';
import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { usePreserveSearchParams } from '@/hooks/use-navigate-with-preserved-search-params';
import DynamicT from '@/shared/components/DynamicT';

import styles from './index.module.scss';

export type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  readonly className?: string;
  readonly children?: ReactNode;
  readonly text?: TFuncKey;
  readonly icon?: ReactNode;
  readonly type?: 'primary' | 'secondary';
} & Partial<LinkProps>;

/**
 * Cowcamo-branded text link. Drop-in replacement for `@/components/TextLink` with the same
 * props, but with cowcamo brand colors and plain link behavior: unlike `TextLink`, it never
 * hijacks `target="_blank"` links into the in-app iframe modal. Links open exactly as the
 * browser would — same tab by default, a new tab when `target="_blank"` is set — so external
 * pages (terms/privacy, password reset) load normally instead of in a blank/blocked modal.
 */
const CowcamoTextLink = ({
  className,
  children,
  text,
  icon,
  type = 'primary',
  to,
  ...rest
}: Props) => {
  const { getTo } = usePreserveSearchParams();

  if (to) {
    return (
      <Link className={classNames(styles.link, styles[type], className)} to={getTo(to)} {...rest}>
        {icon}
        {children ?? <DynamicT forKey={text} />}
      </Link>
    );
  }

  return (
    <a className={classNames(styles.link, styles[type], className)} {...rest} rel="noopener">
      {icon}
      {children ?? <DynamicT forKey={text} />}
    </a>
  );
};

export default CowcamoTextLink;
